import React, { useEffect, useState } from "react";
import styles from "./StatsTab.module.css";
import { getLatestStats, createStats } from "../../../services/stats";
import { Stats } from "../../../types/stats";

/**
 * Admin Stats tab: edits the latest snapshot and saves as a NEW snapshot
 */
export default function StatsTab() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getLatestStats();
        if (mounted && res?.success) {
          // if no data create a skeleton with today as asOf
          setStats(
            res.data || {
              asOf: new Date().toISOString(),
              global: {},
              india: {},
              patna: {},
            }
          );
        }
      } catch (err) {
        console.error("StatsTab load error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const updateField = (path: string, value: string) => {
    setStats((prev) => {
      const clone = JSON.parse(JSON.stringify(prev || {}));
      const keys = path.split(".");
      let cur: any = clone;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = cur[keys[i]] || {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return clone;
    });
  };

  const onSave = async () => {
    if (!stats) return alert("No stats to save");
    setSaving(true);
    try {
      const token = localStorage.getItem("bni_token") || undefined;
      // ensure asOf is set
      if (!stats.asOf) stats.asOf = new Date().toISOString();
      const payload: Stats = {
        ...stats,
        asOf: stats.asOf,
      };

      const res = await createStats(payload, token);
      if (res?.success) {
        alert("Stats snapshot created");
      } else {
        console.error("createStats failed", res);
        alert("Failed to create stats snapshot");
      }
    } catch (err) {
      console.error("onSave error:", err);
      alert("Failed to save stats");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.loading}>Loading …</div>;

  const s = stats || {
    asOf: new Date().toISOString(),
    global: {},
    india: {},
    patna: {},
  };

  const Input = ({
    label,
    path,
    value,
  }: {
    label: string;
    path: string;
    value?: string;
  }) => (
    <div className={styles.inputCard}>
      <label>{label}</label>
      <input
        value={value || ""}
        onChange={(e) => updateField(path, e.target.value)}
        className={styles.input}
      />
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>BNI Performance Dashboard (Admin)</h2>
      <p className={styles.subtitle}>Create a new stats snapshot (history)</p>

      <div className={styles.row}>
        <label>As Of</label>
        <input
          type="date"
          value={new Date(s.asOf).toISOString().slice(0, 10)}
          onChange={(e) =>
            updateField("asOf", new Date(e.target.value).toISOString())
          }
          className={styles.input}
        />
      </div>

      <h3>Global</h3>
      <div className={styles.grid}>
        <Input
          label="Members"
          path="global.members"
          value={s.global?.members}
        />
        <Input
          label="Chapters"
          path="global.chapters"
          value={s.global?.chapters}
        />
        <Input
          label="Countries"
          path="global.countries"
          value={s.global?.countries}
        />
        <Input
          label="Referrals"
          path="global.referrals"
          value={s.global?.referrals}
        />
        <Input
          label="Business Generated"
          path="global.businessGenerated"
          value={s.global?.businessGenerated}
        />
      </div>

      <h3>India</h3>
      <div className={styles.grid}>
        <Input label="Members" path="india.members" value={s.india?.members} />
        <Input
          label="Chapters"
          path="india.chapters"
          value={s.india?.chapters}
        />
        <Input label="Cities" path="india.cities" value={s.india?.cities} />
        <Input
          label="Referrals"
          path="india.referrals"
          value={s.india?.referrals}
        />
        <Input
          label="Business (12m)"
          path="india.businessGenerated"
          value={s.india?.businessGenerated}
        />
        <Input
          label="Avg Seat Value"
          path="india.avgSeatValue"
          value={s.india?.avgSeatValue}
        />
      </div>

      <h3>Patna</h3>
      <div className={styles.grid}>
        <Input label="Members" path="patna.members" value={s.patna?.members} />
        <Input
          label="Chapters"
          path="patna.chapters"
          value={s.patna?.chapters}
        />
        <Input
          label="Referrals"
          path="patna.referrals"
          value={s.patna?.referrals}
        />
        <Input
          label="Visitors"
          path="patna.visitors"
          value={s.patna?.visitors}
        />
        <Input
          label="1-2-1 Meetings"
          path="patna.oneToOne"
          value={s.patna?.oneToOne}
        />
        <Input
          label="Business Generated"
          path="patna.businessGenerated"
          value={s.patna?.businessGenerated}
        />
        <Input
          label="Business (12m)"
          path="patna.businessLast12Months"
          value={s.patna?.businessLast12Months}
        />
      </div>

      <div className={styles.footer}>
        <button className={styles.saveBtn} onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Create Snapshot"}
        </button>
      </div>
    </div>
  );
}
